/**
 * Выпадающее меню (§2.3 «<Menu>»).
 *
 * В отличие от <Select> фокус уходит **внутрь** меню: это набор действий, а не
 * значение поля, и пункты объявляются через `aria-activedescendant` на самом
 * меню (§13). Esc возвращает фокус на триггер — этим занимается <Popover>.
 *
 * Пункт, недоступный по правам, не прячется и не гаснет молча: он остаётся на
 * месте, получает `aria-disabled` и строку-причину под подписью (П4, П6).
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { Popover } from './Popover'
import type { PopoverPlacement } from './Popover'
import './Menu.css'

export interface MenuItem {
  id: string
  label: string
  icon?: ReactNode
  /**
   * Чип значения вместо подписи — там, где пункт меню ставит значение
   * справочника. Текст из `label` остаётся доступным имением пункта.
   */
  badge?: ReactNode
  /** Подпись горячей клавиши справа (§P5: у каждого хоткея видимый эквивалент). */
  hotkey?: string
  /** Необратимое или опасное действие: текст --danger. */
  danger?: boolean
  disabled?: boolean
  /** Причина недоступности: строка 11/14 под подписью. */
  reason?: string
  /** Разделитель перед пунктом. */
  separatorBefore?: boolean
}

export interface MenuProps {
  open: boolean
  anchor: HTMLElement | null
  items: MenuItem[]
  onClose: () => void
  onSelect: (id: string) => void
  /** Доступное имя меню: «Действия с брендом». */
  label: string
  placement?: PopoverPlacement
  minWidth?: number
}

export function Menu({
  open,
  anchor,
  items,
  onClose,
  onSelect,
  label,
  placement = 'bottom-end',
  minWidth = 200,
}: MenuProps) {
  const reactId = useId()
  const baseId = `menu${reactId}`
  const [activeIndex, setActiveIndex] = useState(0)
  const typedRef = useRef('')
  const menuRef = useRef<HTMLDivElement | null>(null)

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? -1 : index))
    .filter((index) => index >= 0)

  /* Фокус переводится на сам список, а не на слой поповера: события клавиатуры
     всплывают от сфокусированного элемента вверх, и обработчик на контейнере
     меню не услышал бы их, если бы фокус остался на обёртке. */
  useEffect(() => {
    if (open) menuRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (open) setActiveIndex(enabledIndexes[0] ?? 0)
    // Пересчёт только на открытии: список пунктов во время показа не меняется.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const itemId = (index: number) => `${baseId}-item-${index}`

  function move(direction: 1 | -1) {
    if (enabledIndexes.length === 0) return
    const current = enabledIndexes.indexOf(activeIndex)
    const next = current < 0 ? 0 : current + direction
    const clamped = Math.min(Math.max(next, 0), enabledIndexes.length - 1)
    setActiveIndex(enabledIndexes[clamped] ?? activeIndex)
  }

  function choose(index: number) {
    const item = items[index]
    if (!item || item.disabled) return
    onSelect(item.id)
    onClose()
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        move(-1)
        return
      case 'Home':
        event.preventDefault()
        setActiveIndex(enabledIndexes[0] ?? 0)
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? 0)
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        choose(activeIndex)
        return
      default:
        break
    }

    /* Печать первой буквы — переход к пункту (§2.3). */
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      typedRef.current = event.key.toLowerCase()
      const hit = items.findIndex(
        (item) => !item.disabled && item.label.toLowerCase().startsWith(typedRef.current),
      )
      if (hit >= 0) {
        event.preventDefault()
        setActiveIndex(hit)
      }
    }
  }

  return (
    <Popover
      open={open}
      anchor={anchor}
      onClose={onClose}
      role="none"
      placement={placement}
      minWidth={minWidth}
    >
      <div
        ref={menuRef}
        className="menu"
        role="menu"
        aria-label={label}
        aria-activedescendant={itemId(activeIndex)}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        {items.map((item, index) => {
          const classes = ['menu__item', 't-cell']
          if (item.danger) classes.push('menu__item--danger')
          if (index === activeIndex) classes.push('menu__item--active')

          return (
            <div key={item.id} role="none">
              {item.separatorBefore ? <div className="menu__separator" role="separator" /> : null}
              <button
                type="button"
                id={itemId(index)}
                role="menuitem"
                tabIndex={-1}
                aria-disabled={item.disabled || undefined}
                aria-describedby={item.reason ? `${itemId(index)}-reason` : undefined}
                className={classes.join(' ')}
                onMouseEnter={() => {
                  if (!item.disabled) setActiveIndex(index)
                }}
                onClick={() => choose(index)}
              >
                {item.icon ? <span className="menu__icon">{item.icon}</span> : null}
                <span className="menu__text">
                  {item.badge
                    ? <span className="menu__badge">{item.badge}</span>
                    : <span className="t-truncate">{item.label}</span>}
                  {item.reason ? (
                    <span id={`${itemId(index)}-reason`} className="menu__reason t-meta">
                      {item.reason}
                    </span>
                  ) : null}
                </span>
                {item.hotkey ? <span className="menu__hotkey t-meta">{item.hotkey}</span> : null}
              </button>
            </div>
          )
        })}
      </div>
    </Popover>
  )
}
